import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AuthLayout from "./AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Spinner } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/formatError";

export default function Signup() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ name: "", organization_name: sp.get("org") || "", email: sp.get("email") || "", password: "" });
  const plan = sp.get("plan");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { signup } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await signup({ ...form, email: form.email.trim() });
      if (plan) { try { localStorage.setItem("wappy.selectedPlan", JSON.stringify({ plan, billing: sp.get("billing"), managed: sp.get("managed") === "1" })); } catch { /* ignore */ } }
      toast.success("Workspace created");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Signup failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your workspace"
      subtitle={plan ? `Starting your free trial on the ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan${sp.get("managed") === "1" ? " with managed campaigns" : ""}.` : "Free to start. Connect your WhatsApp Business number in minutes."}
      footer={<>Already have an account? <Link to="/login" className="link font-medium">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" required><Input required value={form.name} onChange={set("name")} placeholder="Jaineel" data-testid="signup-name-input" /></Field>
          <Field label="Business name"><Input value={form.organization_name} onChange={set("organization_name")} placeholder="Acme Clinics" data-testid="signup-org-input" /></Field>
        </div>
        <Field label="Work email" required><Input type="email" required autoComplete="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="signup-email-input" /></Field>
        <Field label="Password" required hint="At least 8 characters."><Input type="password" required autoComplete="new-password" value={form.password} onChange={set("password")} data-testid="signup-password-input" /></Field>
        {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="signup-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full" data-testid="signup-submit-button">{loading && <Spinner className="text-white" />} Create workspace</Button>
        <p className="text-center text-xs text-muted-foreground">You'll be the workspace owner and can invite your team later.</p>
      </form>
    </AuthLayout>
  );
}
